import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import {
  Popover,
  PopoverButton,
  PopoverOverlay,
  PopoverPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import cn from "clsx";
import { useTranslations } from "next-intl";
import { Fragment } from "react";
import { IoClose } from "react-icons/io5";

import { ProductAttributeInfo } from "@/clients/contentful";
import { defaultDocumentToReactComponentsOptions } from "@/features/contentful/default-document-to-react-component-options";
import { BespokeLink } from "../bespoke-link";

export const AttributePopupInfo: React.FC<{
  data: ProductAttributeInfo;
  attributeName: string;
}> = ({ data, attributeName }) => {
  const t = useTranslations("common");
  return (
    <Popover className="flex flex-col-reverse md:relative">
      <PopoverButton className="grey-80 text-xs italic underline outline-none md:mb-1">
        {t("text-what-is-this")}
      </PopoverButton>
      <Transition>
        <TransitionChild
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
          as={Fragment}
        >
          <PopoverOverlay className="fixed inset-0 z-[49] bg-black/40 md:hidden" />
        </TransitionChild>
        <TransitionChild
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
          as={Fragment}
        >
          <div className="fixed left-0 top-0 z-[51] flex h-screen w-full items-center justify-center md:absolute md:left-full md:top-1/2 md:z-10 md:ml-8 md:size-auto md:!-translate-y-1/2">
            <PopoverPanel>
              {({ close }) => (
                <>
                  <div className="text base border-grey-40 relative flex max-h-[80vh] w-[300px] flex-col rounded-lg border bg-white p-6 shadow-[6px_6px_8px_0px_rgba(0,0,0,0.08)] md:w-[280px]">
                    <p className="!mb-5 text-base font-semibold">{attributeName}</p>
                    <div className="text-sm">
                      {documentToReactComponents(
                        data.fields.description,
                        defaultDocumentToReactComponentsOptions,
                      )}
                    </div>
                    <span className="w-100 bg-grey-40 my-5 block h-px"></span>
                    <BespokeLink
                      className="w-100 !gap-y-0"
                      questionClassName="text-[13px] font-semibold"
                      actionClassName="text-xs font-light"
                    />
                    <button
                      onClick={() => close()}
                      aria-label="Close popover"
                      className={cn(
                        "hover:bg-grey-100 absolute z-10 inline-flex size-8 items-center justify-center rounded-full bg-white shadow-[0_4px_4px_0px_rgba(0,0,0,0.08)] hover:text-white focus:outline-none md:size-8",
                        "-right-3 -top-3 md:-right-4 md:-top-4 md:hidden",
                      )}
                    >
                      <IoClose className="text-2xl text-inherit" />
                    </button>
                  </div>
                  <div className="border-grey-40 absolute left-0 top-1/2 hidden size-7 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-bl-xl border-b border-l bg-white md:block"></div>
                </>
              )}
            </PopoverPanel>
          </div>
        </TransitionChild>
      </Transition>
    </Popover>
  );
};
